/**
 * "Version" selector on the surfacer case panel.
 *
 * Every report-matching version (prompt / LLM / cutoffs) of a family is its own
 * replay cohort over the same image predictions and reports. The surfacer
 * response lists this study's versions in `versions` (web-demo-backend #793):
 * every family cohort the caller can see that holds this study, judged or not,
 * oldest first, each with this study's job id there and the report-model tag.
 * When there is more than one, this opens the same study under another version.
 *
 * The case route carries `<study_uid>::<job_id>` and each replay has its own job
 * rows, so navigation uses the job id the backend gives for that cohort. The
 * `collection` param is dropped on a switch: a collection names specific
 * cohorts, so its Next case would walk away from the version just picked.
 *
 * A backend older than `versions` sends none, and the picker is not shown.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import type { SurfacerVersion } from "../../hooks/useSurfacerCandidates";
import { readerCaseId, readerCaseViewerPath } from "../../utils/readerCaseId";

export interface VersionOption extends SurfacerVersion {
  label: string;
}

/**
 * One option per version, in the order given. Label = the report-model tag; the
 * cohort id is added when the tag is missing or shared by two versions, so the
 * options stay distinct. The current cohort is listed even if the backend ever
 * left it out, so the select always shows where the reader is.
 */
export function versionOptions(
  versions: readonly SurfacerVersion[],
  currentCohortId: string,
): VersionOption[] {
  const all = versions.some((v) => v.cohort_id === currentCohortId)
    ? versions
    : [{ cohort_id: currentCohortId, job_id: "", model: null }, ...versions];
  const tagCount = new Map<string, number>();
  for (const v of all) {
    if (v.model) tagCount.set(v.model, (tagCount.get(v.model) ?? 0) + 1);
  }
  return all.map((v) => ({
    ...v,
    label: !v.model
      ? v.cohort_id
      : (tagCount.get(v.model) ?? 0) > 1
        ? `${v.model} · ${v.cohort_id}`
        : v.model,
  }));
}

/** True when the picker has somewhere to go: another version of this study. */
export function showVersionPicker(versions: readonly SurfacerVersion[] | undefined): boolean {
  return (versions?.length ?? 0) > 1;
}

interface SurfacerVersionPickerProps {
  cohortId: string;
  studyUid: string;
  versions: readonly SurfacerVersion[];
}

const SurfacerVersionPicker: React.FC<SurfacerVersionPickerProps> = ({
  cohortId,
  studyUid,
  versions,
}) => {
  const navigate = useNavigate();
  const options = versionOptions(versions, cohortId);

  return (
    <label className="flex items-center gap-1.5 min-w-0">
      <span className="text-[9px] font-semibold uppercase tracking-wider text-fg-dim">Version</span>
      <select
        aria-label="Version"
        data-testid="surfacer-version-picker"
        value={cohortId}
        onChange={(e) => {
          const target = options.find((o) => o.cohort_id === e.target.value);
          if (!target?.job_id || target.cohort_id === cohortId) return;
          navigate(
            readerCaseViewerPath(
              readerCaseId({ study_uid: studyUid, job_id: target.job_id }),
              target.cohort_id,
            ),
          );
        }}
        title="Open this study under another report-matching version. Verdicts carry across versions."
        className="min-w-0 max-w-56 truncate px-1.5 py-0.5 text-[11px] bg-bg border border-border-strong rounded-xs text-fg focus:outline-none focus:border-primary/50"
      >
        {options.map((o) => (
          <option key={o.cohort_id} value={o.cohort_id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
};

export default SurfacerVersionPicker;
